import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TaskGuideContext } from '../context/TaskGuideContext';
import { WelcomeAPI } from '../lib/api/welcome';
import { queryKeys } from '../lib/query/client';
import type { TaskGuideContextType, TaskGuideResponse } from '../types/taskGuide';

export function useTaskGuide(): TaskGuideContextType | null {
  const context = useContext(TaskGuideContext);
  return context ?? null;
}

export function useTaskGuideTasks(orgId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.taskGuideTasks(orgId),
    queryFn: () => WelcomeAPI.getTasks(),
    enabled,
  });
}

export function useDismissTask(orgId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => WelcomeAPI.dismissTask(taskId),
    onMutate: async (taskId) => {
      const key = queryKeys.taskGuideTasks(orgId);
      await queryClient.cancelQueries({ queryKey: key });

      const prev = queryClient.getQueryData<TaskGuideResponse>(key);
      if (prev) {
        queryClient.setQueryData<TaskGuideResponse>(key, {
          tasks: prev.tasks.map((t) => (t.id === taskId ? { ...t, dismissed: true } : t)),
        });
      }
      return { prev };
    },
    onError: (_err, _id, context) => {
      if (context?.prev) {
        queryClient.setQueryData(queryKeys.taskGuideTasks(orgId), context.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.taskGuideTasks(orgId) });
    },
  });
}
